/**
 * Facts about a party that are COMPUTED from what is stored, rather than stored themselves (MAR-176).
 *
 * ## Why this exists as its own kind
 *
 * A party-block input binds to a fact via `entitySource.field`: `name`, `idNumber`, or
 * `attribute:<key>` — all of which read a value someone recorded. `edad_trabajador` is different:
 * the identity extractor reads a DUI's **date of birth**, never an age, because an age is only true
 * on the day it was written. Every Salvadoran comparecencia nevertheless states the party's age, so
 * every person block in the template library has an `edad_*` field that no stored attribute can fill.
 *
 * Deriving it is safe precisely because it is not stored: the value is computed when the form is
 * filled, so it is correct on the day the document is drafted, and no stale age can be persisted
 * onto an entity and then quietly rot.
 *
 * ## Deliberately a closed set
 *
 * Same rule as `TEMPLATE_TRANSFORMS`: an expression language here would be a second programming
 * model living in the database, unreviewable and unversioned. A derivation is added in code, with a
 * test, or not at all.
 */

import type { CanonicalAttributeKey } from './attributeKeys'

/** The derivations a template input may bind to, as `derived:<key>`. */
export const DERIVED_FACTS = ['age'] as const
export type DerivedFact = (typeof DERIVED_FACTS)[number]

/** Which stored attributes each derivation reads. Used to explain a binding in the review UI, and
 *  to answer "would this actually fill for this party" without running the derivation. */
export const DERIVED_FACT_SOURCES: Record<DerivedFact, CanonicalAttributeKey[]> = {
  age: ['birth_date'],
}

export function isDerivedFact(v: unknown): v is DerivedFact {
  return typeof v === 'string' && (DERIVED_FACTS as readonly string[]).includes(v)
}

function foldToken(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

/**
 * True when `rawKey` names a derived age fact (`age`, `edad`, `derived:age`, `attribute:age`,
 * `attribute:edad`, `<slot>.age`, `<slot>.edad`) that must be computed at render time and never
 * persisted as a static attribute on an entity (MAR-1176 AC2).
 */
export function isDerivedAttributeKey(rawKey: string | null | undefined): boolean {
  if (typeof rawKey !== 'string') return false
  let prop = rawKey.trim()
  if (!prop) return false
  const dotIdx = prop.lastIndexOf('.')
  if (dotIdx !== -1) prop = prop.slice(dotIdx + 1).trim()
  if (prop.startsWith('attribute:')) prop = prop.slice('attribute:'.length).trim()
  else if (prop.startsWith('derived:')) prop = prop.slice('derived:'.length).trim()
  const folded = foldToken(prop).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return folded === 'age' || folded === 'edad' || isDerivedFact(folded)
}

interface CalendarDate {
  year: number
  month: number
  day: number
}

const SPANISH_MONTHS: Record<string, number> = {
  enero: 1,
  febrero: 2,
  marzo: 3,
  abril: 4,
  mayo: 5,
  junio: 6,
  julio: 7,
  agosto: 8,
  septiembre: 9,
  setiembre: 9,
  octubre: 10,
  noviembre: 11,
  diciembre: 12,
}

const SPANISH_DAYS: Record<string, number> = {
  uno: 1,
  un: 1,
  primero: 1,
  dos: 2,
  tres: 3,
  cuatro: 4,
  cinco: 5,
  seis: 6,
  siete: 7,
  ocho: 8,
  nueve: 9,
  diez: 10,
  once: 11,
  doce: 12,
  trece: 13,
  catorce: 14,
  quince: 15,
  dieciseis: 16,
  diecisiete: 17,
  dieciocho: 18,
  diecinueve: 19,
  veinte: 20,
  veintiuno: 21,
  veintiun: 21,
  veintidos: 22,
  veintitres: 23,
  veinticuatro: 24,
  veinticinco: 25,
  veintiseis: 26,
  veintisiete: 27,
  veintiocho: 28,
  veintinueve: 29,
  treinta: 30,
  'treinta y uno': 31,
  'treinta y un': 31,
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

function daysInMonth(year: number, month: number): number {
  switch (month) {
    case 2:
      return isLeapYear(year) ? 29 : 28
    case 4:
    case 6:
    case 9:
    case 11:
      return 30
    default:
      return 31
  }
}

function validCalendarDate(year: number, month: number, day: number): CalendarDate | null {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null
  if (month < 1 || month > 12) return null
  if (day < 1 || day > daysInMonth(year, month)) return null
  // "1900-01-01" is a known placeholder somebody typed, not a real birthday.
  if (year === 1900 && month === 1 && day === 1) return null
  return { year, month, day }
}

function parseBirthCalendarDate(raw: string): CalendarDate | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/.exec(trimmed)
  if (isoMatch) {
    return validCalendarDate(Number(isoMatch[1]), Number(isoMatch[2]), Number(isoMatch[3]))
  }

  const dmyMatch = /^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/.exec(trimmed)
  if (dmyMatch) {
    return validCalendarDate(Number(dmyMatch[3]), Number(dmyMatch[2]), Number(dmyMatch[1]))
  }

  const folded = foldToken(trimmed).replace(/\s+/g, ' ')
  const proseMatch = /^([a-z0-9\s]+?)\s+de\s+([a-z]+)\s+(?:de|del)\s+(\d{4})$/.exec(folded)
  if (proseMatch) {
    const dayToken = proseMatch[1]!.trim()
    const monthToken = proseMatch[2]!.trim()
    const year = Number(proseMatch[3])
    const day = /^\d{1,2}$/.test(dayToken) ? Number(dayToken) : SPANISH_DAYS[dayToken]
    const month = SPANISH_MONTHS[monthToken]
    if (day !== undefined && month !== undefined) {
      return validCalendarDate(year, month, day)
    }
  }

  return null
}

/**
 * Normalize any supported birth-date string (`YYYY-MM-DD`, `DD/MM/YYYY`, or Spanish prose like
 * `"doce de mayo de 1985"`) into canonical ISO `YYYY-MM-DD`, or `null` if unparseable/placeholder.
 */
export function normalizeBirthDate(raw: string | null | undefined): string | null {
  if (!raw) return null
  const parsed = parseBirthCalendarDate(raw)
  if (!parsed) return null
  const y = String(parsed.year).padStart(4, '0')
  const m = String(parsed.month).padStart(2, '0')
  const d = String(parsed.day).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function extractReferenceCalendarDate(reference: Date): CalendarDate | null {
  if (!(reference instanceof Date) || Number.isNaN(reference.getTime())) return null
  const isLocalMidnight = reference.getHours() === 0
    && reference.getMinutes() === 0
    && reference.getSeconds() === 0
    && reference.getMilliseconds() === 0
    && (reference.getUTCHours() !== 0 || reference.getUTCMinutes() !== 0)
  if (isLocalMidnight) {
    return {
      year: reference.getFullYear(),
      month: reference.getMonth() + 1,
      day: reference.getDate(),
    }
  }
  return {
    year: reference.getUTCFullYear(),
    month: reference.getUTCMonth() + 1,
    day: reference.getUTCDate(),
  }
}

/**
 * Whole years between a date of birth and a reference date.
 *
 * `reference` is a parameter rather than `new Date()` inside, so the result is testable and so a
 * caller could one day compute an age as of a document's own date rather than as of today.
 *
 * Returns `''` for anything unparseable, a future date, or an implausible age — the caller writes
 * the empty string, which leaves the field blank for a human. A wrong age in a legal instrument is
 * worse than an empty one, and "1900-01-01" is a placeholder somebody typed, not a birthday.
 */
export function ageFrom(birthDate: string | null | undefined, reference: Date = new Date()): string {
  if (!birthDate) return ''
  const born = parseBirthCalendarDate(birthDate)
  const ref = extractReferenceCalendarDate(reference)
  if (!born || !ref) return ''

  let age = ref.year - born.year
  // Not yet had this year's birthday (in a non-leap year, Feb 28 < Feb 29 and Mar 1 > Feb 29).
  const monthDelta = ref.month - born.month
  if (monthDelta < 0 || (monthDelta === 0 && ref.day < born.day)) age -= 1

  if (age < 0 || age > 120) return ''
  return String(age)
}

/**
 * Resolve a derived fact from a party's stored attributes.
 *
 * Returns `''` when the inputs are missing, which is the same "this party does not have that fact"
 * signal a missing attribute gives — so the fill logic needs no special case, and a field whose
 * derivation cannot run is simply left for the user rather than written blank over their typing.
 */
export function resolveDerivedFact(
  fact: DerivedFact,
  attributes: Partial<Record<CanonicalAttributeKey, string>> | Record<string, string | undefined> | null | undefined,
  reference?: Date,
): string {
  switch (fact) {
    case 'age': {
      const rawAttrs = attributes as Record<string, string | undefined> | null | undefined
      const birthDate = attributes?.birth_date
        ?? rawAttrs?.fecha_nacimiento
        ?? rawAttrs?.fecha_de_nacimiento
      return ageFrom(birthDate, reference)
    }
    default:
      return ''
  }
}
